import React from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MessageCircle, Loader2, AlertCircle } from "lucide-react";

/**
 * v23.18 — Driver-side WhatsApp opt-in toggle. Silences all outbound job/dispute/
 * payment WhatsApp notifications when OFF. Email + in-app notifications continue.
 */
export default function DriverWhatsAppToggle() {
  const { user, refresh } = useAuth();
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  const isOn = user?.whatsapp_opt_in !== false; // default ON (backward compat)

  const flip = async () => {
    setSaving(true);
    setError("");
    try {
      await apiClient.patch("/driver/whatsapp-opt-in", { whatsapp_opt_in: !isOn });
      if (refresh) await refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update preference");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mb-8" data-testid="driver-whatsapp-toggle">
      <div className="bg-white border border-[#0A0A0A]/10 p-5 flex items-start gap-4 flex-wrap">
        <div className="shrink-0 h-10 w-10 bg-[#25D366]/10 border border-[#25D366]/30 flex items-center justify-center">
          <MessageCircle size={18} className="text-[#128C7E]" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#0A0A0A]">
            WhatsApp job alerts
          </p>
          <p className="mt-1 text-sm text-[#475569] max-w-lg">
            Get instant WhatsApp pings when a customer books your quote, pays escrow, or opens a dispute. Turn off if you&apos;d rather rely on email + in-app only.
          </p>
          {error && (
            <div className="mt-2 flex items-start gap-2 text-sm text-[#B91C1C]" data-testid="whatsapp-toggle-error">
              <AlertCircle size={14} className="mt-0.5 shrink-0" /><span>{error}</span>
            </div>
          )}
        </div>
        <button
          onClick={flip}
          disabled={saving}
          data-testid="whatsapp-opt-in-toggle"
          aria-pressed={isOn}
          className={`shrink-0 inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60 border ${
            isOn
              ? "bg-[#25D366]/10 border-[#25D366]/40 text-[#128C7E] hover:bg-[#25D366]/20"
              : "bg-white border-[#0A0A0A]/20 text-[#0A0A0A] hover:bg-[#F5F5F5]"
          }`}
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : (
            <span className={`inline-block h-4 w-8 relative rounded-full transition-colors ${isOn ? "bg-[#25D366]" : "bg-[#0A0A0A]/25"}`}>
              <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${isOn ? "left-4" : "left-0.5"}`} />
            </span>
          )}
          {isOn ? "WhatsApp ON" : "WhatsApp OFF"}
        </button>
      </div>
    </section>
  );
}
